// orders (catalog-outline.md "orders"): the order aggregate of @radish/core/orders on the wire. Every event payload
// decodes to exactly the shape core's `OrderEvent` carries (minus its `type`, which is the event name), so a stored or
// synced fact folds through core's `evolve` with no hand mapping. All commands are device-origin: a register decides
// them alone with the node down (T2) and syncs the events as facts.
import { z } from 'zod';
import { defineCommand, defineQuery } from '../../define/operations.ts';
import { defineError } from '../../define/errors.ts';
import { defineEvent } from '../../define/event.ts';
import { additive } from '../../define/upcast.ts';
import { TaxTreatment } from '../catalog/index.ts';
import { CapabilityUnavailable } from '../../sync/capabilities.ts';
import {
  AdjustmentId, CheckId, CustomerAddressId, CustomerId, EmployeeId, EpochMs, TableId, IsoInstant, LocalDate, LocationId, Milli, Money, OrderId, OrderLineId, PrinterId, ProductId, Qty, Rate,
  TenderId, TillSessionId, VoidReasonId,
} from '../../primitives/codecs.ts';

// ---- shared shapes ---------------------------------------------------------------------------------------------------
const VoidReasonKinds = [
  z.object({ kind: z.literal('customer_request') }),
  z.object({ kind: z.literal('kitchen_error'), ticketNumber: z.int().positive() }),
  z.object({ kind: z.literal('other'), note: z.string().min(1).max(200) }),
] as const;
/** The reasons OrderVoided v1 carried (kept for its upcaster). */
const VoidReasonV1 = z.discriminatedUnion('kind', [...VoidReasonKinds]);
/**
 * Why an order is voided: one of the location's void reasons (Back Office › Voids; its text is copied so a later rename
 * never changes the void), free text ("Other"), or the system kinds.
 */
export const VoidReason = z
  .discriminatedUnion('kind', [...VoidReasonKinds, z.object({ kind: z.literal('listed'), reasonId: VoidReasonId, text: z.string().trim().min(1).max(255) })])
  .meta({ id: 'VoidReason' });

const Currency = z.enum(['USD', 'CAD']).meta({ id: 'Currency' });

/** One modifier on a line, priced (core/catalog `resolveSelection`; target model §1.8 LineModifier). */
const LineModifierOption = z
  .object({
    optionId: z.string().min(1).max(64),
    name: z.string().min(1).max(120),
    kitchenName: z.string().max(120).nullable(),
    unitPrice: Money,
    quantity: z.int().min(1).max(999),
    count: z.int().min(0).max(999),
    side: z.union([z.literal(1), z.literal(2)]),
    presence: z.enum(['add', 'no', 'only']).nullable(),
    portion: z.enum(['extra', 'lite']).nullable(),
    presentation: z.enum(['side', 'tossed']).nullable(),
    included: z.boolean(),
  })
  .meta({ id: 'LineModifierOption' });

export const LineModifierGroup = z
  .object({
    groupId: z.string().min(1).max(64),
    name: z.string().min(1).max(120),
    split: z.boolean(),
    splitText: z.enum(['Side', 'Half']),
    showNameOnKitchenTicket: z.boolean(),
    options: z.array(LineModifierOption).max(200),
  })
  .meta({ id: 'LineModifierGroup' });

/** The modifiers chosen on the card, per set (core/catalog `SelectedGroup`). */
export const ModifierSelection = z
  .object({
    groupId: z.string().min(1).max(64),
    split: z.boolean(),
    options: z.array(z.object({
      optionId: z.string().min(1).max(64),
      quantity: z.int().min(1).max(999),
      side: z.union([z.literal(1), z.literal(2)]),
      presence: z.enum(['add', 'no', 'only']).optional(),
      portion: z.enum(['extra', 'lite']).optional(),
      presentation: z.enum(['side', 'tossed']).optional(),
    })).max(200),
  })
  .meta({ id: 'ModifierSelection' });

const AdjustmentBasis = z
  .union([z.strictObject({ percent: Rate }), z.strictObject({ amount: Money })])
  .meta({ id: 'AdjustmentBasis' });

/** The scheduled price adjustment a line was rung under (W-29): the rule and what it does to the unit price. */
const LinePriceAdjustment = z
  .object({
    ruleId: z.string().min(1).max(64),
    kind: z.enum(['markup', 'markdown', 'fixed_amount']),
    basis: AdjustmentBasis,
  })
  .meta({ id: 'LinePriceAdjustment' });

/** The catalog snapshot a line is sold at; `catalogVersion` is what the stale_config classifier compares. */
const ProductSnapshot = z
  .object({
    productId: ProductId,
    name: z.string().min(1).max(255),
    unitPrice: Money,
    catalogVersion: z.int().nonnegative(),
    /** How the price relates to sales tax when rung; absent on lines rung before it was snapshotted (priced exempt). */
    taxTreatment: TaxTreatment.optional(),
    // v3 (lane D1): what else the line was rung with. Absent on older lines.
    itemKind: z.enum(['standard', 'unit', 'custom', 'misc', 'gift_card_load', 'house_account_payment']).optional(),
    variantId: z.string().min(1).max(64).optional(),
    variantName: z.string().max(120).optional(),
    unitCountMilli: Milli.optional(),
    modifiers: z.array(LineModifierGroup).max(50).optional(),
    kitchenName: z.string().max(120).optional(),
    cashPriceEntered: z.boolean().optional(),
    ageRestrictionId: z.string().min(1).max(64).optional(),
    courseId: z.string().min(1).max(64).optional(),
    // v4 (lane D2): a `gift_card_load` line's card (legacy kept it in the line note).
    giftCardNumber: z.string().regex(/^\d{12,20}$/).optional(),
    // v6 (lane D2): the scheduled price adjustment the line was rung under (legacy `item.price_adjustment`).
    priceAdjustment: LinePriceAdjustment.optional(),
    // LineAdded v7 / LineUpdated v4 (lane D2b): a `house_account_payment` line's customer (legacy parsed it from the
    // line note "Name (hashId)", B32).
    houseAccountCustomerId: CustomerId.optional(),
    // LineAdded v8 / LineUpdated v5 (lane D3): a misc line's ticked printers (legacy `misc_order_item_printer`); absent,
    // the printers preselected for misc items.
    printerIds: z.array(PrinterId).max(50).optional(),
    // LineAdded v10 / LineUpdated v6 (lane RS, N5): a custom line rung at its listed price while the card's cash toggle
    // was on (legacy `cash_price_toggle`, the listed price fixed at a tax profile changed afterwards); a reopen re-lists it.
    cashPriceToggle: z.boolean().optional(),
  })
  .meta({ id: 'ProductSnapshot' });

/**
 * A product snapshot as a reader from before the house-account payment line takes it (LineAdded v6, LineUpdated v3):
 * the same money as a misc line of that name and price, without the customer it pays for.
 */
const snapshotWithoutHouseAccount = (product: unknown): unknown => {
  const p = product as { readonly itemKind?: unknown };
  if (p.itemKind !== 'house_account_payment') return product;
  return { ...Object.fromEntries(Object.entries(p).filter(([key]) => key !== 'houseAccountCustomerId')), itemKind: 'misc' };
};
const withoutHouseAccount = (newer: unknown): unknown => {
  const p = newer as { readonly product?: unknown };
  return p.product === undefined ? newer : { ...p, product: snapshotWithoutHouseAccount(p.product) };
};

/**
 * A line as a reader from before misc printers takes it (LineAdded v7, LineUpdated v4): without the ticked printers, so
 * an older register sends the misc line to the printers preselected for misc items.
 */
const withoutMiscPrinters = (newer: unknown): unknown => {
  const p = newer as { readonly product?: Record<string, unknown> };
  if (p.product === undefined || !('printerIds' in p.product)) return newer;
  return { ...p, product: Object.fromEntries(Object.entries(p.product).filter(([key]) => key !== 'printerIds')) };
};

/**
 * A line as a reader from before the custom cash toggle takes it (LineAdded v9, LineUpdated v5): the same listed price,
 * without the toggle, so an older register reopens the line on the listed price as it did before (N5).
 */
const withoutCashToggle = (newer: unknown): unknown => {
  const p = newer as { readonly product?: Record<string, unknown> };
  if (p.product === undefined || !('cashPriceToggle' in p.product)) return newer;
  return { ...p, product: Object.fromEntries(Object.entries(p.product).filter(([key]) => key !== 'cashPriceToggle')) };
};
/** Every line of `lines` as a reader from before the custom cash toggle takes it (OrdersAdopted v2, OrderCarriedOver v2). */
const linesWithoutCashToggle = (lines: unknown): unknown => (Array.isArray(lines) ? lines.map(withoutCashToggle) : lines);

/** OrderAbandoned v2 → v1: unchanged for `eod_auto_close`; `force_complete` cannot be said in v1. */
const abandonedForV1 = (newer: unknown): unknown => {
  if ((newer as { readonly cause?: unknown }).cause === 'force_complete') throw new Error('OrderAbandoned v1 has no force_complete cause');
  return newer;
};

const OrderNumber = z
  .object({ seriesId: z.string().min(1).max(64), seq: z.int().nonnegative(), display: z.string().max(32) })
  .meta({ id: 'OrderNumber' });

/** One check's money before payments (target-model §2.6), card price; `cashTotal` is the True Margin cash price. */
const CheckTotals = z
  .object({
    checkId: CheckId, checkNumber: z.int().positive(), subtotal: Money, discounts: Money, serviceCharges: Money, tax: Money, total: Money,
    cashTotal: Money,
  })
  .meta({ id: 'CheckTotals' });

const PricingTotalsV1 = z.object({
  rulesVersion: z.int().nonnegative(), subtotal: Money, discounts: Money, serviceCharges: Money, tax: Money, writeOffs: Money, total: Money,
});

const PricingTotals = PricingTotalsV1.extend({
  /** A split order's checks (S9); absent when the order is one check. */
  checks: z.array(CheckTotals).optional(),
  /**
   * v3 (lane D5 round 41, B458 / L622): each line whose subtotal legacy's API would store differently from the charge (a
   * half-and-half set's halves summed once; core `legacyStoredLineGross`), which Item, Category and Department Sales read.
   * Absent when no line differs, and on orders priced before it (reports keep the charge).
   */
  legacyItemGross: z.array(z.object({ lineId: OrderLineId, amount: Money })).optional(),
  /**
   * v4 (lane RP, RP3a): each percent-discounted line whose discount legacy's API would store differently from the charge
   * (the unrounded per-unit discount times the quantity, rounded once; core `legacyStoredLineDiscount`), which legacy's
   * reports read. Absent when no line differs, and on orders priced before it (reports keep the charge).
   */
  legacyItemDiscount: z.array(z.object({ lineId: OrderLineId, amount: Money })).optional(),
  /**
   * v4 (lane RP, RP3): off True Margin, the tax and total legacy's API stores when the register sends the order, where they
   * differ from the charge (core `legacyCreateTotals`), which legacy's reports read. Absent when they agree. v6 (lane RP,
   * B658): `gratuity`, the gratuity it stores where it differs from the charge (also setting the totals when only the
   * gratuity differs), which legacy's register caps a gratuity payout at.
   */
  legacyStoredTotals: z.object({ tax: Money, total: Money, cartDiscount: Money.optional(), gratuity: Money.optional() }).optional(),
  /**
   * v5 (lane RP, RP5n): every line's tax as legacy's API stores it (`order_items.tax`: its rate on the line after its own
   * discount, the cart discount and gratuity taxed at the order only; core `legacyApiLineTaxes`), which legacy's Employee
   * Sales breakdown and Refund Details read. Absent where core cannot rebuild it, and on orders priced before it.
   */
  legacyItemTax: z.array(z.object({ lineId: OrderLineId, amount: Money })).optional(),
}).meta({ id: 'PricingTotals' });

/** An OrderPriced wire payload's totals without the named fields (v4 → v3 drops `legacyItemDiscount`). */
function withoutTotals(newer: unknown, keys: readonly string[]): unknown {
  if (newer === null || typeof newer !== 'object' || !('totals' in newer)) return newer;
  const totals = (newer as { totals: unknown }).totals;
  if (totals === null || typeof totals !== 'object') return newer;
  return { ...newer, totals: Object.fromEntries(Object.entries(totals).filter(([key]) => !keys.includes(key))) };
}

/** An OrderPriced v6 wire payload as v5: the legacy stored totals without their `gratuity` (B658). */
function withoutStoredGratuity(newer: unknown): unknown {
  if (newer === null || typeof newer !== 'object' || !('totals' in newer)) return newer;
  const totals = (newer as { totals: unknown }).totals;
  if (totals === null || typeof totals !== 'object' || !('legacyStoredTotals' in totals)) return newer;
  const stored = (totals as { legacyStoredTotals: unknown }).legacyStoredTotals;
  if (stored === null || typeof stored !== 'object') return newer;
  const rest = Object.fromEntries(Object.entries(stored).filter(([key]) => key !== 'gratuity'));
  // Set only for the gratuity (its tax and total the charge's, no cart discount): v5 had no stored totals for it.
  const same = (key: 'tax' | 'total') => JSON.stringify(rest[key]) === JSON.stringify((totals as Record<string, unknown>)[key]);
  if (same('tax') && same('total') && !('cartDiscount' in rest)) {
    return { ...newer, totals: Object.fromEntries(Object.entries(totals).filter(([key]) => key !== 'legacyStoredTotals')) };
  }
  return { ...newer, totals: { ...totals, legacyStoredTotals: rest } };
}

/** An OrderPriced v3 wire payload as v2 (the same totals without `legacyItemGross`). */
function withoutLegacyItemGross(newer: unknown): unknown {
  if (newer === null || typeof newer !== 'object' || !('totals' in newer)) return newer;
  const totals = (newer as { totals: unknown }).totals;
  if (totals === null || typeof totals !== 'object') return newer;
  return { ...newer, totals: Object.fromEntries(Object.entries(totals).filter(([key]) => key !== 'legacyItemGross')) };
}

const OrderLine = z
  .object({ lineId: OrderLineId, productId: ProductId, name: z.string(), quantity: Qty, unitPrice: Money, lineTotal: Money })
  .meta({ id: 'OrderLine' });

export const OrderSnapshot = z
  .object({
    orderId: OrderId,
    locationId: LocationId,
    status: z.enum(['open', 'sent', 'closed', 'voided', 'abandoned']),
    /** The display number (`42`, or `R2-0007` from a device's own series). */
    number: z.string(),
    tabName: z.string().nullable(),
    /** The table the order is seated at (lane D3); absent when it is at none. */
    tableId: TableId.optional(),
    lines: z.array(OrderLine),
    total: Money,
    paid: Money,
    balanceDue: Money,
    version: z.int().nonnegative(),
  })
  .meta({ id: 'OrderSnapshot' });

/**
 * Every command that answers with an order's snapshot (C8): v2 added the display number, the tab name and the paid
 * and balance-due totals. A v1 result came from the Phase 1 stub, which had no numbering, tabs or payments.
 */
const orderOutput = {
  output: OrderSnapshot,
  outputVersion: 2,
  outputUpcastFrom: [{
    version: 1,
    up: (old: unknown) => {
      const v1 = old as { readonly total: { readonly currency: string } };
      return { number: '', tabName: null, paid: { minor: '0', currency: v1.total.currency }, balanceDue: v1.total, ...(old as object) };
    },
  }],
} as const;

// ---- errors ----------------------------------------------------------------------------------------------------------
export const OrderNotFound = defineError({
  code: 'orders.not_found', httpStatus: 404, message: 'errors.orders.not_found', retryable: false,
  details: z.object({ orderId: OrderId }),
});
export const OrderAlreadyClosed = defineError({
  code: 'orders.already_closed', httpStatus: 409, message: 'errors.orders.already_closed', retryable: false,
  details: z.object({ orderId: OrderId, status: z.enum(['closed', 'voided', 'abandoned']) }),
});
export const TenderSettled = defineError({
  code: 'orders.tender_settled', httpStatus: 409, message: 'errors.orders.tender_settled', retryable: false,
  details: z.object({ orderId: OrderId, settledTenders: z.int().positive() }),
});
export const ProductNotFound = defineError({
  code: 'orders.product_not_found', httpStatus: 404, message: 'errors.orders.product_not_found', retryable: false,
  details: z.object({ productId: ProductId }),
});
/** The card's choices do not make a line (core/catalog `RingError`: a missing variant, a required modifier set, …). */
export const LineNotRingable = defineError({
  code: 'orders.line_not_ringable', httpStatus: 422, message: 'errors.orders.line_not_ringable', retryable: false,
  details: z.object({ reason: z.string(), groupId: z.string().optional() }),
});
export const OrderTillNotOpen = defineError({
  code: 'orders.till_not_open', httpStatus: 409, message: 'errors.orders.till_not_open', retryable: false,
  details: z.object({ tillSessionId: TillSessionId }),
});
export const BalanceDue = defineError({
  code: 'orders.balance_due', httpStatus: 409, message: 'errors.orders.balance_due', retryable: false,
  details: z.object({ due: Money }),
});
/** Any other rule core's order machine refuses (`rule` is core's error code, e.g. `line_not_found`, `nothing_to_send`). */
export const OrderRuleViolation = defineError({
  code: 'orders.rule_violation', httpStatus: 409, message: 'errors.orders.rule_violation', retryable: false,
  details: z.object({ rule: z.string() }),
});

// ---- events ----------------------------------------------------------------------------------------------------------
const order = { aggregate: 'order', deviceConsumed: true } as const;

/** Stands in for a till session on an order opened before till sessions existed (OrderOpened v1, the Phase 1 stub). */
const NO_TILL = '00000000-0000-7000-8000-000000000000';

const OrderOpenedV1 = z.object({ orderId: z.string(), locationId: z.string(), openedByEmployeeId: z.string(), openedAt: z.string() });

const OrderOpenedPayload = z.object({
  orderId: OrderId,
  currency: Currency,
  number: OrderNumber,
  /** v3 (lane D3, ops plan 2026-10-13-online-orders-in-reports): `online`, an online order recorded on the location. */
  channel: z.enum(['pos', 'online']),
  /** An online order names core's ONLINE_ORDER_OWNER_ID here: no employee owns it. */
  ownerEmployeeId: EmployeeId,
  tillSessionId: TillSessionId,
  /** v4 (lane D2, A54): `hideNameOnReceipt`, legacy's "Hide name on customer receipt" (`tabs.receipt_print_order`); absent is false. */
  tab: z.object({ name: z.string().min(1).max(255), isBarTab: z.boolean(), hideNameOnReceipt: z.boolean().optional() }).optional(),
  diningOptionId: z.string().optional(),
  openedAt: EpochMs,
  businessDate: LocalDate,
  /**
   * v3: an online order's tip, which legacy's stored order total includes (its void amount). v5 (lane RG, B1584): a QR
   * Table Pay order's table, shown in Order Lookup and the order detail only. It never seats the order (legacy's tab names
   * the table with `linkOrderToTable: false`), so the table never reads occupied; absent: no table.
   */
  online: z.object({ tip: Money, tableId: TableId.optional() }).optional(),
});

/**
 * An OrderOpened v3 payload as a v2 reader takes it: an online order reads as a register order (`pos`) without its tip,
 * so a register on an older build keeps syncing and counts it as any order.
 */
const openedAsPos = (newer: unknown): unknown => {
  const p = newer as Record<string, unknown>;
  return { ...Object.fromEntries(Object.entries(p).filter(([key]) => key !== 'online')), channel: 'pos' };
};

/** An OrderOpened v5 payload as a v4 reader takes it: an online order without its shown table (it shows none, as before). */
const openedWithoutOnlineTable = (newer: unknown): unknown => {
  const p = newer as Record<string, unknown>;
  const online = p['online'] as Record<string, unknown> | undefined;
  return online === undefined ? p : { ...p, online: Object.fromEntries(Object.entries(online).filter(([key]) => key !== 'tableId')) };
};

/** An OrderOpened v4 payload as a v3 reader takes it: the tab without `hideNameOnReceipt` (it prints the name, as before). */
const openedWithoutHiddenName = (newer: unknown): unknown => {
  const p = newer as Record<string, unknown>;
  const tab = p['tab'] as Record<string, unknown> | undefined;
  return tab === undefined ? p : { ...p, tab: Object.fromEntries(Object.entries(tab).filter(([key]) => key !== 'hideNameOnReceipt')) };
};

export const OrderOpened = defineEvent({
  ...order,
  name: 'OrderOpened',
  version: 5,
  summary: 'An order (a tab, when named) was opened on a till session, with its number for the business date; or an online order was recorded on the location.',
  payload: OrderOpenedPayload,
  // B1681 (lane RG): an online order's tip is money (core's stored and reported order totals add it), so it is verifiable.
  verifiable: ['orderId', 'currency', 'tillSessionId', 'businessDate', 'online.tip', 'online.tableId'],
  merge: 'lifecycle',
  // v1 was written only by the Phase 1 server stub, which had no tills, currency or numbering. v3 widened the channel
  // to online orders and added their tip, so every v2 payload is a v3 payload. v4 (lane D2) added the tab's
  // `hideNameOnReceipt`, so every v3 payload is a v4 payload. v5 (lane RG, B1584) added an online order's shown
  // `online.tableId`, so every v4 payload is a v5 payload.
  upcastFrom: [
    {
      version: 1,
      up: (old) => {
        const v1 = OrderOpenedV1.parse(old);
        return {
          orderId: v1.orderId, currency: 'USD' as const, number: { seriesId: 'v1', seq: 0, display: '' }, channel: 'pos' as const,
          ownerEmployeeId: v1.openedByEmployeeId, tillSessionId: NO_TILL, openedAt: v1.openedAt, businessDate: v1.openedAt.slice(0, 10),
        };
      },
    },
    additive(2),
    additive(3),
    additive(4),
  ],
  downcastTo: [{ version: 4, down: openedWithoutOnlineTable }, { version: 3, down: openedWithoutHiddenName }, { version: 2, down: openedAsPos }],
});

export const ItemAdded = defineEvent({
  ...order,
  name: 'ItemAdded',
  version: 1,
  summary: 'Superseded by LineAdded (written only by the Phase 1 server stub); kept so stored v1 facts stay readable.',
  payload: z.object({
    orderId: OrderId,
    lineId: OrderLineId,
    product: z.object({ productId: ProductId, name: z.string(), unitPrice: Money }),
    quantity: Qty,
    addedByEmployeeId: EmployeeId,
    addedAt: IsoInstant,
  }),
  verifiable: ['product.unitPrice', 'quantity'],
  merge: 'commutative',
});

const LineAddedPayload = z.object({
  lineId: OrderLineId, product: ProductSnapshot, qtyMilli: Milli, note: z.string().max(500).optional(), addedByEmployeeId: EmployeeId.optional(),
  /** The check it was rung on (a by-item split). */
  checkId: CheckId.optional(),
  /**
   * v9 (B1343): true when a payment already counted on the order as the line was rung. Only such a line takes a
   * configured automatic discount past the tender lock, as legacy puts one on only as the item is added. Absent: rung
   * before any payment (every v8 line reads so).
   */
  rungAfterPayment: z.literal(true).optional(),
});

/** LineAdded v9 -> v8: a v8 reader has no line age; it decides an automatic discount as it did before. */
const withoutLineAge = (newer: unknown): unknown =>
  Object.fromEntries(Object.entries(newer as Record<string, unknown>).filter(([key]) => key !== 'rungAfterPayment'));

export const LineAdded = defineEvent({
  ...order,
  name: 'LineAdded',
  version: 10,
  summary: 'A line was added to an order at the catalog snapshot it was rung against.',
  payload: LineAddedPayload,
  verifiable: [
    'lineId', 'product.productId', 'product.unitPrice', 'product.taxTreatment', 'product.unitCountMilli', 'product.modifiers', 'product.priceAdjustment',
    'qtyMilli',
  ],
  merge: 'commutative',
  // v2 added the snapshot's tax treatment (a v1 line carries none and keeps pricing as exempt). v3 (lane D1) added the
  // variant, unit count, modifiers and open-price facts and the line note. v4 added the check it was rung on (a by-item
  // split); all optional, so a v3 payload is a v4 payload, on the first check. v5 (lane D2) added the gift-card load
  // kind and its card number, optional too: a v4 payload is a v5 payload. v6 (lane D2) added the price adjustment the
  // line was rung under, optional: a v5 line was rung under none. v7 (lane D2b) added the house-account payment kind
  // and its customer; a v6 reader takes such a line as a misc line of the same price. Each step only added optional
  // fields (two lanes shipped different v3 shapes; v4 is their union), so every older payload is already a valid next
  // one. v8 (lane D3) added a misc line's ticked printers, optional: a v7 misc line goes to the printers preselected
  // for misc items. v9 (B1343) added `rungAfterPayment`, optional: a v8 line reads as rung before any payment (strict),
  // and a v8 reader is sent the line without it. v10 (lane RS, N5) added the snapshot's `cashPriceToggle`, optional: a
  // v9 custom line was rung without it, and a v9 reader is sent the line without it (the same listed price).
  upcastFrom: [
    additive(1), additive(2),
    { version: 3, up: (old) => old as z.input<typeof LineAddedPayload> },
    { version: 4, up: (old) => old as z.input<typeof LineAddedPayload> },
    additive(5), additive(6), additive(7), additive(8), additive(9),
  ],
  downcastTo: [
    { version: 9, down: withoutCashToggle }, { version: 8, down: withoutLineAge }, { version: 7, down: withoutMiscPrinters },
    { version: 6, down: withoutHouseAccount },
  ],
  pii: ['product.giftCardNumber'],
});

export const LineUpdated = defineEvent({
  ...order,
  name: 'LineUpdated',
  version: 6,
  summary: 'A line\'s quantity was raised, its note changed, or it was re-rung in place with new choices (Update Item).',
  payload: z.object({
    lineId: OrderLineId, qtyMilli: Milli.optional(), note: z.string().max(500).optional(),
    /** v2 (lane D1, AT-MOD-010): the snapshot the line is re-rung at; the line keeps its id and what was sent. */
    product: ProductSnapshot.optional(),
  }),
  verifiable: [
    'lineId', 'qtyMilli', 'product.productId', 'product.unitPrice', 'product.taxTreatment', 'product.unitCountMilli', 'product.modifiers',
    'product.priceAdjustment',
  ],
  merge: { exclusive: 'lineId' },
  // v2 only added the optional re-rung snapshot: a v1 payload is a v2 payload. v3 (lane D2) added the snapshot's optional
  // price adjustment (the line keeps the one it was rung under): a v2 payload is a v3 payload. v4 (lane D2b) the snapshot's
  // house-account payment kind and customer, read by a v3 reader as a misc line. v5 (lane D3) a misc line's ticked
  // printers, dropped for a v4 reader. v6 (lane RS, N5) the snapshot's custom cash toggle, dropped for a v5 reader.
  upcastFrom: [additive(1), additive(2), additive(3), additive(4), additive(5)],
  downcastTo: [{ version: 5, down: withoutCashToggle }, { version: 4, down: withoutMiscPrinters }, { version: 3, down: withoutHouseAccount }],
});

export const LineVoided = defineEvent({
  ...order,
  name: 'LineVoided',
  version: 1,
  summary: 'Some or all of a line was voided (a removed line is a void with the removal reason).',
  payload: z.object({
    lineId: OrderLineId,
    qtyMilli: Milli,
    voidedByEmployeeId: EmployeeId.optional(),
    approvedByEmployeeId: EmployeeId.optional(),
    wasSentToKitchen: z.boolean(),
    reasonId: z.string().optional(),
    reasonText: z.string().max(500).optional(),
  }),
  // B1687 (lane RG): who voided the line (the command's employee) and the reason it names are decided the same way at every
  // receiver. Not the approver (re-verification decides without one; SEC-60 judges it, override.ts), and not
  // wasSentToKitchen: core reads it from the order's state, which honestly differs when another register sent the line
  // before this one heard of it.
  verifiable: ['lineId', 'qtyMilli', 'voidedByEmployeeId', 'reasonId'],
  merge: { exclusive: 'lineId' },
});

const DiscountSource = z.enum(['automatic', 'preset', 'custom']);

/** An AdjustmentApplied v2 wire payload as v1 (the same fields without `source`). */
function omitSource(newer: unknown): unknown {
  if (newer === null || typeof newer !== 'object') return newer;
  return Object.fromEntries(Object.entries(newer).filter(([key]) => key !== 'source'));
}

export const AdjustmentApplied = defineEvent({
  ...order,
  name: 'AdjustmentApplied',
  version: 2,
  summary: 'A discount, comp, service charge or write-off was applied to an order or a line.',
  payload: z.object({
    adjustmentId: AdjustmentId,
    kind: z.enum(['discount', 'comp', 'service_charge', 'write_off']),
    scope: z.enum(['order', 'line']),
    lineId: OrderLineId.optional(),
    basis: AdjustmentBasis,
    reason: z.string().max(500).optional(),
    writeOffReason: z.enum(['unpaid_balance', 'capture_failed', 'manager']).optional(),
    tenderId: TenderId.optional(),
    /** v2 (lane D2): automatic, preset or custom, for the Close Drawer report's Discount Summary; absent before. */
    source: DiscountSource.optional(),
    appliedByEmployeeId: EmployeeId.optional(),
    approvedByEmployeeId: EmployeeId.optional(),
  }),
  // B1687 (lane RG): which line it sits on, its source (the Close Drawer Discount Summary's axis), a write-off's reason and
  // tender, and who applied it (the command's employee) are all decided from the command, so every receiver agrees. Not
  // the approver: re-verification decides without one, and SEC-60 judges it (override.ts). A v1 fact has no `source`,
  // and this receiver's decision is compared as v1 carries it (sync/verify.ts asCarriedBy).
  verifiable: ['adjustmentId', 'kind', 'scope', 'lineId', 'basis', 'writeOffReason', 'tenderId', 'source', 'appliedByEmployeeId'],
  merge: 'commutative',
  // v2 only added the optional source: a v1 payload is a v2 payload, and a register that reads v1 gets it without one.
  upcastFrom: [additive(1)],
  downcastTo: [{ version: 1, down: (newer) => omitSource(newer) }],
});

export const AdjustmentRemoved = defineEvent({
  ...order,
  name: 'AdjustmentRemoved',
  version: 1,
  summary: 'An adjustment was removed from an order.',
  payload: z.object({ adjustmentId: AdjustmentId, removedByEmployeeId: EmployeeId.optional() }),
  verifiable: ['adjustmentId'],
  merge: { exclusive: 'adjustmentId' },
});

export const OrderPriced = defineEvent({
  ...order,
  name: 'OrderPriced',
  version: 6,
  summary: 'The order\'s money after the command that changed it, as pricing computed it.',
  payload: z.object({ pricingSeq: z.int().positive(), totals: PricingTotals }),
  verifiable: ['totals.subtotal', 'totals.discounts', 'totals.serviceCharges', 'totals.tax', 'totals.writeOffs', 'totals.total', 'totals.legacyItemGross', 'totals.legacyItemDiscount', 'totals.legacyStoredTotals', 'totals.legacyItemTax'],
  merge: 'idempotent_set',
  // v2 added the per-check totals of a split order; a v1 fact priced an order that was one check. v3 (lane D5) added the
  // optional legacy item gross: a v2 payload is a v3 payload, and a register that reads v2 gets it without one. v4 (lane
  // RP) added the optional legacy item discount the same way, v5 (lane RP, RP5n) the optional legacy item tax, and v6 (lane
  // RP, B658) the optional stored gratuity inside the legacy stored totals.
  upcastFrom: [{ version: 1, up: (old) => old as z.input<typeof PricingTotals> as never }, additive(2), additive(3), additive(4), additive(5)],
  downcastTo: [
    { version: 5, down: withoutStoredGratuity },
    { version: 4, down: (newer) => withoutTotals(newer, ['legacyItemTax']) },
    { version: 3, down: (newer) => withoutTotals(newer, ['legacyItemDiscount', 'legacyStoredTotals', 'legacyItemTax']) }, { version: 2, down: withoutLegacyItemGross },
  ],
});

export const DiningOptionChanged = defineEvent({
  ...order,
  name: 'DiningOptionChanged',
  version: 1,
  summary: 'The order\'s dining option changed.',
  payload: z.object({ diningOptionId: z.string() }),
  verifiable: ['diningOptionId'],
  merge: 'idempotent_set',
});

export const TaxProfileChanged = defineEvent({
  ...order,
  name: 'TaxProfileChanged',
  version: 1,
  summary: "Adjust Tax Profile: every line of the order is taxed this way, or (null) by its own item again.",
  payload: z.object({ taxProfile: TaxTreatment.nullable() }),
  verifiable: ['taxProfile'],
  merge: 'idempotent_set',
});

export const TabRenamed = defineEvent({
  ...order,
  name: 'TabRenamed',
  version: 2,
  summary: 'A tab was renamed, and with v2 its "Hide name on customer receipt" set or cleared.',
  /** v2 (lane D2, A54): `hideNameOnReceipt` when the rename set it; absent keeps what the order had. */
  payload: z.object({ name: z.string().min(1).max(255), hideNameOnReceipt: z.boolean().optional() }),
  verifiable: [],
  merge: 'idempotent_set',
  // A v1 rename carries no setting; a v1 reader drops it and keeps printing the name, as before.
  upcastFrom: [additive(1)],
  downcastTo: [{ version: 1, down: (newer) => Object.fromEntries(Object.entries(newer as Record<string, unknown>).filter(([key]) => key !== 'hideNameOnReceipt')) }],
});

/** Lane D3: the order moved to another till session (drawer or server bank), another owner, or both. */
export const OrderTransferred = defineEvent({
  ...order,
  name: 'OrderTransferred',
  version: 1,
  summary: 'An order was transferred to another drawer or server bank, another server, or both.',
  payload: z.object({
    fromTillSessionId: TillSessionId, tillSessionId: TillSessionId, fromOwnerEmployeeId: EmployeeId, ownerEmployeeId: EmployeeId,
  }),
  verifiable: ['tillSessionId', 'ownerEmployeeId'],
  merge: 'idempotent_set',
});

/** Lane D3: the order was seated at a table, moved to another, or taken off its table (null). */
export const TableChanged = defineEvent({
  ...order,
  name: 'TableChanged',
  version: 1,
  summary: 'An order was seated at a table, moved to another table, or taken off its table.',
  payload: z.object({ tableId: TableId.nullable() }),
  verifiable: [],
  merge: 'idempotent_set',
});

/**
 * The customer on an order (W-69): their id, the address picked and the display name, and (v2 of CustomerChanged) the
 * phone and address the order's receipts print, as they were when the customer was attached (legacy keeps them on the
 * tab: `tab_phone`, `tab_address_1`, `tab_address_2`; a later edit to the customer leaves the order as it was). Absent
 * when the customer has none, or on a fact from before v2.
 */
export const OrderCustomer = z
  .object({
    customerId: CustomerId,
    addressId: CustomerAddressId.nullable(),
    name: z.string().trim().min(1).max(255),
    phone: z.string().min(1).max(32).optional(),
    address1: z.string().min(1).max(255).optional(),
    address2: z.string().min(1).max(255).optional(),
  })
  .meta({ id: 'OrderCustomer' });

/** The fields `CustomerChanged` v2 added (the customer's phone and address): a reader of v1 takes the payload without them. */
const customerAsV1 = (newer: unknown): unknown => {
  const payload = newer as { readonly customer: Record<string, unknown> | null };
  if (payload.customer === null) return payload;
  const added = new Set(['phone', 'address1', 'address2']);
  return { ...payload, customer: Object.fromEntries(Object.entries(payload.customer).filter(([k]) => !added.has(k))) };
};

export const CustomerChanged = defineEvent({
  ...order,
  name: 'CustomerChanged',
  version: 2,
  summary: 'A customer was attached to an order (with the address picked), replaced, or taken off it.',
  payload: z.object({ customer: OrderCustomer.nullable() }),
  verifiable: [],
  // The customer's phone and address are their contact details (lane D2 r53, coordinator 2026-10-16): never logged.
  pii: ['customer.phone', 'customer.address1', 'customer.address2'],
  // v1 facts carry no phone nor address: still valid, read as absent.
  upcastFrom: [additive(1)],
  downcastTo: [{ version: 1, down: customerAsV1 }],
  merge: 'idempotent_set',
});

export const SentToKitchen = defineEvent({
  ...order,
  name: 'SentToKitchen',
  version: 1,
  summary: 'A round was sent to the bar or kitchen: the unsent quantity of each line.',
  payload: z.object({ sendId: z.string().min(1).max(64), lines: z.array(z.object({ lineId: OrderLineId, qtyMilliDelta: Milli })) }),
  verifiable: ['lines'],
  merge: 'commutative',
});

/**
 * Lane D3 (W-30 order pacing): a line put in another course, or taken out of any (`null`), on the product card's Courses
 * page or in the Course Selection dialog. The line keeps it over the product's own course.
 */
export const LineCourseChanged = defineEvent({
  ...order,
  name: 'LineCourseChanged',
  version: 1,
  summary: "A line's course was set (order pacing), or cleared.",
  payload: z.object({ lineId: OrderLineId, courseId: z.string().min(1).max(64).nullable() }),
  verifiable: [],
  merge: { exclusive: 'lineId' },
});

/** Lane D3 (W-31 hold & fire): a line held back from the kitchen, or released (fired) to it. */
export const LineHoldChanged = defineEvent({
  ...order,
  name: 'LineHoldChanged',
  version: 1,
  summary: 'A line was held back from the kitchen (Hold), or released to it (Fire All).',
  payload: z.object({ lineId: OrderLineId, held: z.boolean() }),
  verifiable: [],
  merge: { exclusive: 'lineId' },
});

const OrderVoidedV1 = z.object({ orderId: z.string(), reason: VoidReasonV1, voidedByEmployeeId: z.string(), voidedAt: z.string() });

export const OrderVoided = defineEvent({
  ...order,
  name: 'OrderVoided',
  version: 2,
  summary: 'An order was voided.',
  payload: z.object({
    voidedByEmployeeId: EmployeeId.optional(),
    approvedByEmployeeId: EmployeeId.optional(),
    voidedAt: EpochMs,
    reasonId: z.string().optional(),
    reasonText: z.string().max(500).optional(),
  }),
  // B1681 (lane RG): who voided (the command's employee) and the reason it names are references the receiver decides
  // the same way. The approver is not: re-verification decides without one, and SEC-60 judges the approver (override.ts).
  // B1687 N1: a v1 fact (the Phase 1 server stub's only, before any register existed) upcasts without `reasonId` and has
  // no way down (v1 needs the order id and a v1 reason), so a v1 push naming a listed reason would read as a mismatch.
  verifiable: ['voidedByEmployeeId', 'reasonId'],
  merge: 'lifecycle',
  upcastFrom: [{
    version: 1,
    up: (old) => {
      const v1 = OrderVoidedV1.parse(old);
      return { voidedByEmployeeId: v1.voidedByEmployeeId, voidedAt: v1.voidedAt, reasonText: voidReasonText(v1.reason) };
    },
  }],
});

/** An OrderClosed wire payload without the named fields (v3 → v2 drops `storedTotals`; v2 → v1 drops `tax`). */
function omitClosedFields(newer: unknown, keys: readonly string[]): unknown {
  if (newer === null || typeof newer !== 'object') return newer;
  return Object.fromEntries(Object.entries(newer).filter(([key]) => !keys.includes(key)));
}

export const OrderClosed = defineEvent({
  ...order,
  name: 'OrderClosed',
  version: 3,
  summary: 'An order was closed: paid in full, or with what was still due written off.',
  payload: z.object({
    closedAt: EpochMs,
    /**
     * v2 (lane D1 r50): a paid split True Margin order's tax as legacy stores it, the whole order's card tax at what its
     * checks paid (core `OrderPricer.paidTax`); absent elsewhere. Reports read it over the priced tax less each cash
     * tender's moved tax.
     */
    tax: Money.optional(),
    /**
     * v3 (lane RP, RP1): a paid True Margin order's total and tax as legacy's API stores them (CompleteOrder reprices the
     * order with its tenders: `orders.total`, `orders.tax`; core `storedTotalsOf`), priced under the config the order was
     * taken under. Every v3 close carries it: null off True Margin, on a write-off or forced close, on an online sale and
     * where it cannot be priced (the recorded figures stand). Absent only on a close recorded before v3, which reports
     * read as the priced figures less what the tenders redeemed and moved.
     */
    storedTotals: z.object({ tax: Money, total: Money }).nullable().optional(),
    paid: Money, balance: Money, forced: z.boolean(), reviewRequired: z.boolean(),
  }),
  // `tax` is verified from v2 on and `storedTotals` from v3: a receiver compares an older register's close as its version
  // carries it (sync verify.ts, lane X).
  verifiable: ['tax', 'storedTotals', 'paid', 'balance', 'forced'],
  merge: 'lifecycle',
  // v2 and v3 each only added an optional field: an older payload is a newer one, read without it.
  upcastFrom: [additive(1), additive(2)],
  downcastTo: [
    { version: 2, down: (newer) => omitClosedFields(newer, ['storedTotals']) },
    { version: 1, down: (newer) => omitClosedFields(newer, ['tax']) },
  ],
});

export const OrderReopened = defineEvent({
  ...order,
  name: 'OrderReopened',
  version: 1,
  summary: 'A closed order was reopened.',
  payload: z.object({ reopenedByEmployeeId: EmployeeId.optional(), reason: z.string().max(500).optional() }),
  verifiable: [],
  merge: 'lifecycle',
});

export const OrderAbandoned = defineEvent({
  ...order,
  name: 'OrderAbandoned',
  // v2 (DLG-073): the cause widens with `force_complete`; every v1 payload is a v2 one.
  version: 2,
  upcastFrom: [additive(1)],
  // A v1 register still gets the business-day close's abandoned orders, unchanged; v1 has no word for force_complete
  // (only under the switched-off correction), so that one is refused rather than relabelled (review of #1543).
  downcastTo: [{ version: 1, down: abandonedForV1 }],
  summary: 'The business-day close (or a reconciled force complete) abandoned an order no payment was taken on.',
  // `force_complete` (DLG-073): only under core's FORCE_COMPLETE 'reconciled', the correction shipped switched off.
  payload: z.object({ cause: z.enum(['eod_auto_close', 'force_complete']), job: z.string().max(64), abandonedAt: EpochMs }),
  verifiable: [],
  merge: 'lifecycle',
});

// ---- checks (split checks, W-45 / W-46; target-model §1.1) ---------------------------------------------------------------

const SplitMode = z.enum(['even', 'by_item']).meta({ id: 'SplitMode' });

export const ChecksSplit = defineEvent({
  ...order,
  name: 'ChecksSplit',
  version: 1,
  summary: 'An order was split into checks: evenly (every line shared), or by item (every line starts on the first check).',
  payload: z.object({ mode: SplitMode, checks: z.array(z.object({ checkId: CheckId, checkNumber: z.int().positive() })).min(2) }),
  verifiable: ['mode', 'checks'],
  merge: 'lifecycle',
});

export const CheckAdded = defineEvent({
  ...order,
  name: 'CheckAdded',
  version: 1,
  summary: 'An empty check was added to an order split by item.',
  payload: z.object({ checkId: CheckId, checkNumber: z.int().positive() }),
  verifiable: ['checkId'],
  merge: 'lifecycle',
});

export const CheckRemoved = defineEvent({
  ...order,
  name: 'CheckRemoved',
  version: 1,
  summary: 'An empty check was removed; with one check left the order is no longer split.',
  payload: z.object({ checkId: CheckId }),
  verifiable: ['checkId'],
  merge: 'lifecycle',
});

const LineMovePayload = z.object({
  lineId: OrderLineId,
  qtyMilli: Milli,
  fromCheckId: CheckId,
  toCheckId: CheckId,
  /** Moving part of a line splits it: the moved part becomes this line (and its discount this adjustment). */
  newLineId: OrderLineId.optional(),
  newAdjustmentId: AdjustmentId.optional(),
});

export const LinesMovedBetweenChecks = defineEvent({
  ...order,
  name: 'LinesMovedBetweenChecks',
  version: 1,
  summary: 'Lines (or part of a line) moved to another check of an order split by item.',
  payload: z.object({ moves: z.array(LineMovePayload).min(1) }),
  verifiable: ['moves'],
  merge: 'lifecycle',
});

export const ChecksMerged = defineEvent({
  ...order,
  name: 'ChecksMerged',
  version: 1,
  summary: 'An order\'s checks were combined into one (Combine Checks, Unsplit Payment); its payments now pay the whole order.',
  payload: z.object({ checkIds: z.array(CheckId), intoCheckId: CheckId }),
  verifiable: ['checkIds', 'intoCheckId'],
  merge: 'lifecycle',
});

// ---- Combine Orders (WF-40; ops plan 2026-10-14-combine-orders, option (c)) -----------------------------------------------

/** A line as an order holds it (core `OrderLine`), adopted whole by Combine Orders. */
const AdoptedLine = z.object({
  lineId: OrderLineId,
  product: ProductSnapshot,
  qtyMilli: Milli,
  sentQtyMilli: Milli,
  voidedQtyMilli: Milli,
  note: z.string().max(500).optional(),
  courseId: z.string().min(1).max(64).nullable().optional(),
  held: z.boolean().optional(),
});

/** An adjustment as an order holds it (core `OrderAdjustment`): a line's discount, or, with B355's fix on, a cart one. */
/**
 * An adjustment's basis inside `OrdersAdopted`: the percent or the amount the order held (core always holds one). Not
 * the `AdjustmentBasis` union: zod 4 throws encoding an amount basis through that union nested in these arrays (it runs
 * the percent branch's Rate codec on the missing field), so a register could not record a combine of a discounted line.
 */
const AdoptedBasis = z.object({ percent: Rate.optional(), amount: Money.optional() });

const AdoptedAdjustment = z.object({
  adjustmentId: AdjustmentId,
  kind: z.enum(['discount', 'comp', 'service_charge', 'write_off']),
  scope: z.enum(['order', 'line']),
  lineId: OrderLineId.optional(),
  basis: AdoptedBasis,
  active: z.boolean(),
  reason: z.string().max(500).optional(),
  writeOffReason: z.enum(['unpaid_balance', 'capture_failed', 'manager']).optional(),
  tenderId: TenderId.optional(),
  source: DiscountSource.optional(),
});

export const OrdersAdopted = defineEvent({
  ...order,
  name: 'OrdersAdopted',
  version: 3,
  summary:
    'Combine Orders: this order adopted other open orders by id, with their lines (and the line discounts that go with them) as they stood, and their payments, which now pay this order. Only this order\'s stream is written; each target reads as combined into it.',
  payload: z.object({
    targets: z.array(z.object({
      orderId: OrderId,
      /** The target stream version the deciding replica had seen: a target fact above it was made without seeing the combine. */
      version: z.int().nonnegative(),
      lines: z.array(AdoptedLine).max(500),
      adjustments: z.array(AdoptedAdjustment).max(500),
      /** Orders the target had itself adopted: their payments pay this order too. */
      adoptedOrderIds: z.array(OrderId).max(100).optional(),
    })).min(1).max(50),
    tenderIds: z.array(TenderId).max(500),
  }),
  verifiable: ['targets', 'tenderIds'],
  merge: 'lifecycle',
  // v2 changed only how an adjustment's basis is declared (AdoptedBasis): the wire form is the same, so a v1 payload is a
  // v2 payload and a v1 reader takes a v2 payload as it is. No v1 fact was recorded (no node decided a combine before v2).
  // v3 (lane RS, N5): an adopted line's snapshot may carry the custom cash toggle; a v2 reader takes the lines without it.
  upcastFrom: [additive(1), additive(2)],
  downcastTo: [
    {
      version: 2,
      down: (newer) => {
        const o = newer as { readonly targets?: unknown };
        if (!Array.isArray(o.targets)) return newer;
        return { ...o, targets: o.targets.map((t: unknown) => ({ ...(t as Record<string, unknown>), lines: linesWithoutCashToggle((t as { lines?: unknown }).lines) })) };
      },
    },
    { version: 1, down: (newer) => newer },
  ],
});

/**
 * The field `OrderCarriedOver` v2 added to each line: a reader of v1 takes every carried unit as sent. A tab with a unit
 * legacy had not sent cannot reach it so (it would never fire to the kitchen): the downcast refuses, and the receiver
 * holds that reader's feed (`update_required`) until it updates (lane X's review of #1086).
 */
const carriedAsV1 = (newer: unknown): unknown => {
  const o = newer as { lines?: unknown };
  if (!Array.isArray(o.lines)) return newer;
  const unsent = o.lines.some((l: unknown) => {
    const line = l as { qtyMilli?: unknown; sentQtyMilli?: unknown };
    return typeof line.sentQtyMilli === 'string' && typeof line.qtyMilli === 'string' && BigInt(line.sentQtyMilli) < BigInt(line.qtyMilli);
  });
  if (unsent) throw new Error('OrderCarriedOver v1 cannot carry units not yet sent to the kitchen');
  return { ...o, lines: o.lines.map((l: unknown) => Object.fromEntries(Object.entries(l as Record<string, unknown>).filter(([key]) => key !== 'sentQtyMilli'))) };
};

/**
 * Cutover (lane X's carry-over step; D2 round 38, coordinator option B): an owner-approved legacy tab left open at T0,
 * carried over as a live order on its own stream. A fresh open (any imported snapshot before it is dropped) with legacy's
 * number, owner, open time and lines as rung, each with the units legacy's register had already sent to the kitchen (v2,
 * lane M round 29: `order_items.printed_quantity`; absent, every unit sent); its discounts follow as AdjustmentApplied and its
 * money as OrderPriced, priced as legacy's register prices a reopened cart and held to legacy's stored total to the cent.
 */
export const OrderCarriedOver = defineEvent({
  ...order,
  name: 'OrderCarriedOver',
  version: 3,
  summary: 'A legacy tab carried over at cutover: a fresh open order with legacy’s number, owner, open time and lines (with the units legacy had sent to the kitchen), priced again as legacy’s register prices a reopened cart.',
  payload: z.object({
    orderId: OrderId,
    currency: Currency,
    legacyOrderId: z.int().nonnegative(),
    number: OrderNumber,
    ownerEmployeeId: EmployeeId.optional(),
    openedAt: EpochMs,
    businessDate: LocalDate,
    diningOptionId: z.string().optional(),
    tab: z.object({ name: z.string().min(1).max(255), isBarTab: z.boolean(), hideNameOnReceipt: z.boolean().optional() }).optional(),
    lines: z.array(z.object({
      lineId: OrderLineId, product: ProductSnapshot, qtyMilli: Milli, voidedQtyMilli: Milli,
      /** v2: the units legacy's register had sent to the kitchen; the first send here fires only the rest. Absent: every unit sent. */
      sentQtyMilli: Milli.optional(),
    })).min(1).max(2_000),
  }),
  verifiable: ['orderId', 'legacyOrderId', 'lines'],
  merge: 'lifecycle',
  // v1 lines carried no sent units: still valid, read as every unit sent. v3 (lane RS, N5) lets a line's snapshot carry
  // the custom cash toggle (the import sets `cashPriceEntered` instead today); a v2 reader takes the lines without it.
  upcastFrom: [additive(1), additive(2)],
  // A register on a build that reads v1 takes every carried unit as sent (no re-fire; legacy's unsent units do not fire).
  downcastTo: [
    { version: 2, down: (newer) => ({ ...(newer as Record<string, unknown>), lines: linesWithoutCashToggle((newer as { lines?: unknown }).lines) }) },
    { version: 1, down: carriedAsV1 },
  ],
});

/**
 * B1698 (ops plan 2026-10-30-capture-after-write-off, slice 2): a card the order's write-off booked was captured after
 * all, so its applied amount (never its tip) came back out of that write-off, at most what remained. One fact per
 * write-off drawn on; the order's stored totals follow as OrderPriced, with no repricing.
 */
export const WriteOffOffset = defineEvent({
  ...order,
  name: 'WriteOffOffset',
  version: 1,
  summary: "A captured card's amount taken back out of the order's write-off, so the order is never both paid and written off.",
  payload: z.object({ adjustmentId: AdjustmentId, tenderId: TenderId, amount: Money }),
  verifiable: ['adjustmentId', 'tenderId', 'amount'],
  merge: { exclusive: 'tenderId' },
});

/** Every event the order aggregate records, in the order core's `OrderEvent` union lists them. */
export const orderEvents = [
  OrderOpened, LineAdded, LineUpdated, LineVoided, AdjustmentApplied, AdjustmentRemoved, OrderPriced, DiningOptionChanged, TabRenamed,
  TableChanged, OrderTransferred, SentToKitchen, OrderVoided, OrderClosed, OrderReopened, OrderAbandoned, ChecksSplit, CheckAdded, CheckRemoved, LinesMovedBetweenChecks,
  ChecksMerged, TaxProfileChanged, CustomerChanged, LineCourseChanged, LineHoldChanged, OrdersAdopted, OrderCarriedOver, WriteOffOffset,
] as const;

/** The reason text a VoidReason records on the order (`other` keeps its note). */
export function voidReasonText(reason: z.output<typeof VoidReason>): string {
  switch (reason.kind) {
    case 'customer_request':
      return 'customer_request';
    case 'kitchen_error':
      return `kitchen_error #${reason.ticketNumber}`;
    case 'other':
      return reason.note;
    case 'listed':
      return reason.text;
  }
}

// ---- commands --------------------------------------------------------------------------------------------------------
const onOrder = { stream: { aggregate: 'order', id: (input: { orderId: string }) => input.orderId } } as const;
const device = { availability: 'location', origin: 'device' } as const;
const quiet = { risk: 'low', dryRun: false, agent: { expose: false } } as const;
const editErrors = [OrderNotFound, OrderAlreadyClosed, OrderRuleViolation] as const;

export const OpenOrder = defineCommand({
  ...device, ...quiet,
  name: 'orders.open',
  summary: 'Open an order on an open till session; a name makes it a tab. The device mints the order id (UUIDv7).',
  input: z.object({
    orderId: OrderId, locationId: LocationId, tillSessionId: TillSessionId,
    tabName: z.string().min(1).max(255).optional(), diningOptionId: z.string().optional(),
    /** Legacy's "Hide name on customer receipt" for the name given (`tabs.receipt_print_order`); absent is false. */
    hideNameOnReceipt: z.boolean().optional(),
    /** Opens the order seated at this table (legacy Tables page, map-employee): OrderOpened, then TableChanged. */
    tableId: TableId.optional(),
  }),
  ...orderOutput,
  permission: { session: 'staff' },
  stream: { aggregate: 'order', id: (input) => input.orderId },
  emits: [OrderOpened, OrderPriced],
  errors: [OrderTillNotOpen, OrderRuleViolation],
  legacy: ['POST /v1/orders'],
});

export const AddItem = defineCommand({
  ...device, ...quiet, ...onOrder,
  name: 'orders.addItem',
  summary: 'Add a product line to an open order at the location\'s current catalog price (on a split order, to the check it names).',
  input: z.object({
    orderId: OrderId, lineId: OrderLineId, productId: ProductId, quantity: Qty,
    // lane D1: the product card's choices (core/catalog `RingRequest`).
    variantId: z.string().min(1).max(64).optional(),
    modifiers: z.array(ModifierSelection).max(50).optional(),
    /** Unit items: units entered, thousandths. */
    unitCount: Qty.optional(),
    /** Custom-priced items: the price keyed at the till. */
    openPrice: Money.optional(),
    cashPriceEntered: z.boolean().optional(),
    /** Custom-priced items (N5): `openPrice` is the listed price, rung while the card's cash toggle was on (not converted). */
    cashPriceToggle: z.boolean().optional(),
    taxTreatment: TaxTreatment.optional(),
    note: z.string().max(500).optional(),
    /** The check it is rung on (a by-item split). */
    checkId: CheckId.optional(),
  }),
  ...orderOutput,
  permission: { session: 'staff' },
  emits: [LineAdded, OrderPriced],
  errors: [...editErrors, ProductNotFound, LineNotRingable],
  legacy: ['POST /v1/orders/{id}/items'],
});

export const AddMiscItem = defineCommand({
  ...device, ...quiet, ...onOrder,
  name: 'orders.addMiscItem',
  summary: 'Add a misc line ("Add Misc. Item"): a typed name and price with no catalog product.',
  input: z.object({
    orderId: OrderId, lineId: OrderLineId, quantity: Qty, name: z.string().max(255).optional(), price: Money, taxTreatment: TaxTreatment,
    cashPriceEntered: z.boolean().optional(), note: z.string().max(500).optional(), checkId: CheckId.optional(),
    /**
     * B1763: the misc card's cash toggle was on, but `price` is the listed price (a tax profile chosen after the amount
     * was keyed); kept on the snapshot so the line reopens on the toggle, as legacy keeps `cash_price_toggle`.
     */
    cashPriceToggle: z.boolean().optional(),
    /** The printers ticked on the misc card (legacy Printers multi-select); absent, the printers preselected for misc items. */
    printerIds: z.array(PrinterId).max(50).optional(),
  }),
  ...orderOutput,
  permission: { session: 'staff' },
  emits: [LineAdded, OrderPriced],
  errors: [...editErrors, LineNotRingable],
  legacy: ['POST /v1/orders/{id}/items'],
});

/** The product card's choices on a line being re-rung (AddItem's, less the check). */
const cardChoices = {
  variantId: z.string().min(1).max(64).optional(),
  modifiers: z.array(ModifierSelection).max(50).optional(),
  unitCount: Qty.optional(),
  openPrice: Money.optional(),
  cashPriceEntered: z.boolean().optional(),
  cashPriceToggle: z.boolean().optional(),
  taxTreatment: TaxTreatment.optional(),
} as const;

export const UpdateItem = defineCommand({
  ...device, ...quiet, ...onOrder,
  name: 'orders.updateItem',
  summary: 'Update Item on a line already on the order (sent or not): re-ring it in place with the card\'s new choices and note, or raise its count.',
  input: z.object({
    orderId: OrderId, lineId: OrderLineId, productId: ProductId, ...cardChoices,
    /** A raised count (a lower one is `orders.voidLine`). */
    quantity: Qty.optional(),
    note: z.string().max(500).optional(),
  }),
  ...orderOutput,
  permission: { session: 'staff' },
  emits: [LineUpdated, LineVoided, OrderPriced],
  errors: [...editErrors, ProductNotFound, LineNotRingable],
  legacy: ['PUT /v1/orders/{id} (an item with its id and new modifiers)'],
});

export const UpdateMiscItem = defineCommand({
  ...device, ...quiet, ...onOrder,
  name: 'orders.updateMiscItem',
  summary: 'Update Item on a misc line already on the order: its new name, price and note, in place (Q-PRICE-08).',
  input: z.object({
    orderId: OrderId, lineId: OrderLineId, name: z.string().max(255).optional(), price: Money, taxTreatment: TaxTreatment,
    cashPriceEntered: z.boolean().optional(), quantity: Qty.optional(), note: z.string().max(500).optional(),
    /** B1763: as AddMiscItem's. */
    cashPriceToggle: z.boolean().optional(),
    /** The printers ticked on the misc card; absent, the printers preselected for misc items. */
    printerIds: z.array(PrinterId).max(50).optional(),
  }),
  ...orderOutput,
  permission: { session: 'staff' },
  emits: [LineUpdated, LineVoided, OrderPriced],
  errors: [...editErrors, LineNotRingable],
  legacy: ['PUT /v1/orders/{id} (a misc item with its id)'],
});

/** Sell or reload a gift card (register W-71 "Load Card"): a non-revenue line the gift-card ledger posts when the order closes. */
export const AddGiftCardLoad = defineCommand({
  ...device, ...quiet, ...onOrder,
  name: 'orders.addGiftCardLoad',
  summary: 'Add a "Gift Card" line that loads money onto a card when the order is paid (tax-exempt, never discounted).',
  // The card the money goes onto is part of the sale fact itself (legacy kept it in the line note), so it rides this
  // device-origin command and its LineAdded event; it is not a D7 credential field. The ledger keeps only a digest.
  input: z.object({ orderId: OrderId, lineId: OrderLineId, giftCardNumber: z.string().regex(/^\d{12,40}$/), amount: Money, quantity: Qty }),
  output: OrderSnapshot,
  permission: { session: 'staff' },
  emits: [LineAdded, OrderPriced],
  errors: editErrors,
  legacy: ['POST /v1/orders/{id}/items (product_type gift)'],
});

/**
 * Collect Payment on a customer's house account (register W-70, "Apply Payment"): a tax-exempt "House Account" line
 * for the amount, which credits the account when the order is paid (legacy `product_type: house`, its customer only in
 * the line note; here it is the line's customer id, B32). Sold one at a time, never discounted.
 */
export const AddHouseAccountPayment = defineCommand({
  ...device, ...quiet, ...onOrder,
  name: 'orders.addHouseAccountPayment',
  summary: 'Add a "House Account" line that pays the amount onto a customer house account when the order is paid.',
  input: z.object({ orderId: OrderId, lineId: OrderLineId, customerId: CustomerId, customerName: z.string().max(255), amount: Money }),
  output: OrderSnapshot,
  permission: { session: 'staff' },
  emits: [LineAdded, OrderPriced],
  errors: editErrors,
  legacy: ['POST /v2/orders (an item named "House Account", product_type house)'],
});

export const VoidLine = defineCommand({
  ...device, ...quiet, ...onOrder,
  name: 'orders.voidLine',
  summary: 'Void some or all of a line. A line already sent to the bar needs the void permission.',
  input: z.object({ orderId: OrderId, lineId: OrderLineId, quantity: Qty, reasonId: VoidReasonId.optional(), reasonText: z.string().min(1).max(500).optional() }),
  ...orderOutput,
  permission: { session: 'staff' },
  emits: [LineVoided, OrderPriced],
  errors: editErrors,
  legacy: ['POST /v1/orders/{id}/items/{item}/void'],
});

export const RenameTab = defineCommand({
  ...device, ...quiet, ...onOrder,
  name: 'orders.renameTab',
  summary: 'Rename a tab, and set or clear its "Hide name on customer receipt".',
  input: z.object({
    orderId: OrderId, name: z.string().min(1).max(255),
    /** Legacy `updateTab`'s `receipt_print_order`: present sets it, absent keeps the order's setting. */
    hideNameOnReceipt: z.boolean().optional(),
  }),
  ...orderOutput,
  permission: { session: 'staff' },
  emits: [TabRenamed],
  errors: editErrors,
});

/** "Select a destination drawer or an employee to transfer to." */
export const TransferDestinationRequired = defineError({
  code: 'orders.transfer_destination_required', httpStatus: 422, message: 'errors.orders.transfer_destination_required', retryable: false,
  details: z.object({ orderId: OrderId }),
});

export const TransferOrder = defineCommand({
  ...device, ...quiet, ...onOrder,
  name: 'orders.transfer',
  summary: 'Transfer an order to another drawer or server bank, another server, or both (bulk transfer sends one per order).',
  input: z.object({ orderId: OrderId, tillSessionId: TillSessionId.optional(), ownerEmployeeId: EmployeeId.optional() }),
  output: OrderSnapshot,
  permission: 'orders.transfer',
  emits: [OrderTransferred],
  errors: [...editErrors, TransferDestinationRequired, OrderTillNotOpen],
  legacy: [
    'POST /v1/orders/{id}/transfer', 'POST /v1/orders/{id}/transfer-drawer', 'POST /v1/orders/{id}/transfer-employee', 'POST /v1/drawers/{id}/transfer',
    'POST /v1/drawers/{id}/transfer-open', 'POST /v1/drawers/bulk-order-transfer',
  ],
});

export const SeatAtTable = defineCommand({
  ...device, ...quiet, ...onOrder,
  name: 'orders.seatAtTable',
  summary: 'Seat an order at a table, move it to another table, or take it off its table.',
  input: z.object({ orderId: OrderId, tableId: TableId.nullable() }),
  output: OrderSnapshot,
  permission: { session: 'staff' },
  emits: [TableChanged],
  errors: editErrors,
  legacy: ['PUT /v2/tabs/{id}', 'POST /v1/floor-plans/items/map-employee'],
});

export const SetOrderCustomer = defineCommand({
  ...device, ...quiet, ...onOrder,
  name: 'orders.setCustomer',
  summary: 'Attach a customer to an order with one of their saved addresses, or take the customer off it.',
  input: z.object({ orderId: OrderId, customer: OrderCustomer.nullable() }),
  output: OrderSnapshot,
  permission: { session: 'staff' },
  emits: [CustomerChanged],
  errors: editErrors,
  legacy: ['POST /v1/tabs', 'DELETE /v1/tabs/{id}'],
});

export const SetDiningOption = defineCommand({
  ...device, ...quiet, ...onOrder,
  name: 'orders.setDiningOption',
  summary: "Set the order's dining option (Dine In, Take Out): the cart's Dining Option chip and its prompt.",
  input: z.object({ orderId: OrderId, diningOptionId: z.string().min(1).max(64) }),
  output: OrderSnapshot,
  permission: { session: 'staff' },
  emits: [DiningOptionChanged, OrderPriced],
  errors: editErrors,
  legacy: ['PUT /v1/orders/{id}'],
});

export const SetTaxProfile = defineCommand({
  ...device, ...quiet, ...onOrder,
  name: 'orders.setTaxProfile',
  summary: "Adjust Tax Profile: tax every line of the order one way (exclusive, inclusive, exempt), or by its own item (null).",
  // `exclusive_item`: the register's automatic switch on adding a tax-exclusive item with the location's Tax-exclusive cart
  // override on (legacy `override_tax_profile`), which core allows without `change_tax_profiles`.
  input: z.object({ orderId: OrderId, taxProfile: TaxTreatment.nullable(), cause: z.enum(['staff', 'exclusive_item']).optional() }),
  output: OrderSnapshot,
  permission: 'orders.changeTaxProfile',
  emits: [TaxProfileChanged, OrderPriced],
  errors: editErrors,
  legacy: ['PUT /v1/orders/{id}'],
});

export const SendToKitchen = defineCommand({
  ...device, ...quiet, ...onOrder,
  name: 'orders.sendToKitchen',
  summary: 'Send the unsent quantity of every line that is not held to the bar or kitchen (with courses, only the courses fired).',
  input: z.object({
    orderId: OrderId, sendId: z.uuid({ version: 'v7' }),
    /**
     * Lane D3 (W-30): the courses fired by this send (Course Selection, "Select Courses to Fire"). Lines in other courses
     * stay unsent; a line in no course always goes. Absent: every unsent line goes.
     */
    courseIds: z.array(z.string().min(1).max(64)).max(50).optional(),
  }),
  ...orderOutput,
  permission: { session: 'staff' },
  emits: [SentToKitchen],
  errors: editErrors,
  legacy: ['POST /v2/orders (course_ids)', 'PUT /v2/orders/{id} (course_ids)'],
});

/** Lane D3 (W-30): put a line in a course, or take it out of any. */
export const SetLineCourse = defineCommand({
  ...device, ...quiet, ...onOrder,
  name: 'orders.setLineCourse',
  summary: "Put a line in a course (the product card's Courses page, or Course Selection's Missing Course Assignment), or in none.",
  input: z.object({ orderId: OrderId, lineId: OrderLineId, courseId: z.string().min(1).max(64).nullable() }),
  ...orderOutput,
  permission: { session: 'staff' },
  emits: [LineCourseChanged],
  errors: editErrors,
  legacy: ['PUT /v2/orders/{id} (items.*.course_id)'],
});

/** Lane D3 (W-31): Hold / Held on the product card. Only a line the kitchen has not been sent can be held. */
export const HoldLine = defineCommand({
  ...device, ...quiet, ...onOrder,
  name: 'orders.holdLine',
  summary: 'Hold a line back from the kitchen (it is saved but not sent until fired), or take the hold off before it is sent.',
  input: z.object({ orderId: OrderId, lineId: OrderLineId, held: z.boolean() }),
  ...orderOutput,
  permission: { session: 'staff' },
  emits: [LineHoldChanged],
  errors: editErrors,
  legacy: ['PUT /v2/orders/{id} (items.*.held)'],
});

/** Lane D3 (W-31): Fire All on order details. The register prints what it fired as rushed. */
export const FireHeld = defineCommand({
  ...device, ...quiet, ...onOrder,
  name: 'orders.fireHeld',
  summary: "Fire every held line to the kitchen (order details' Fire All): the holds come off and their unsent quantity is sent.",
  input: z.object({ orderId: OrderId, sendId: z.uuid({ version: 'v7' }) }),
  ...orderOutput,
  permission: { session: 'staff' },
  emits: [LineHoldChanged, SentToKitchen],
  errors: editErrors,
  legacy: ['PUT /v2/orders/{id}/fire-held'],
});

/** The order-pacing commands (courses, hold and fire), registered in the catalog as one group. */
export const pacingCommands = [SetLineCourse, HoldLine, FireHeld] as const;

export const VoidOrder = defineCommand({
  ...device, ...onOrder,
  name: 'orders.void',
  summary: 'Void an order. A closed order is voided only after its payments are voided or refunded.',
  input: z.object({ orderId: OrderId, reason: VoidReason }),
  ...orderOutput,
  permission: 'orders.void',
  risk: 'high',
  emits: [OrderVoided],
  errors: [OrderNotFound, OrderAlreadyClosed, TenderSettled, OrderRuleViolation],
  dryRun: true,
  agent: { expose: true, surfaces: ['backoffice.assistant'], description: 'Void an order a guest walked out on or that was rung in error.' },
  legacy: ['POST /v1/orders/{id}/void2'],
});

/**
 * orders.void for an order this register does not hold (an order the legacy import brought over), decided by the node
 * once its payments are voided (`payments.voidTenderAtRegister`; ops plan 2026-10-13-refund-imported-orders, slice 4).
 * The acting employee rides in the input; the node checks that employee's own grants (the approving manager's).
 */
export const VoidOrderAtRegister = defineCommand({
  availability: 'location', origin: 'any', ...onOrder,
  name: 'orders.voidAtRegister',
  summary: 'orders.void for an order this register does not hold, decided by the node. The acting employee (or approving manager) needs the void permission.',
  input: VoidOrder.input.extend({ employeeId: EmployeeId, approvedByEmployeeId: EmployeeId.optional() }),
  ...orderOutput,
  permission: { session: 'device' },
  risk: 'high',
  emits: [OrderVoided],
  errors: [OrderNotFound, OrderAlreadyClosed, TenderSettled, OrderRuleViolation],
  dryRun: true,
  agent: { expose: false },
  legacy: ['POST /v1/orders/{id}/void'],
});

/**
 * B1698: the node takes a captured card's amount back out of the order's write-off, in the same transaction as the
 * retried capture it records (a companion of `payments.recordCaptureResultAtRegister`), as the employee that capture
 * names. Only a card the write-off booked, once, never more than remains written off. Never sent by a register itself.
 */
export const OffsetWriteOffAtRegister = defineCommand({
  availability: 'location', origin: 'any', ...onOrder, ...quiet,
  name: 'orders.offsetWriteOffAtRegister',
  summary: "Take a retried card's captured amount back out of its order's write-off, decided by the node with the capture.",
  input: z.object({ orderId: OrderId, tenderId: TenderId, employeeId: EmployeeId, approvedByEmployeeId: EmployeeId.optional() }),
  ...orderOutput,
  permission: { session: 'device' },
  emits: [WriteOffOffset, OrderPriced],
  errors: editErrors,
  legacy: ['FinalizeDrawerTips (captures a card whatever the order; legacy has no write-off)'],
});

export const CloseOrder = defineCommand({
  ...device, ...onOrder,
  name: 'orders.close',
  summary:
    'Close an order: paid in full; with less than $1.00 still due, left unpaid (create_discount); or (with the force-complete permission) with what is still due written off.',
  input: z.object({
    orderId: OrderId,
    settlement: z.discriminatedUnion('mode', [
      z.object({ mode: z.literal('paid') }),
      /**
       * WF-47, legacy Order Details ⋮ Close Order (`OrderCompleteController`): the order is marked complete with its
       * total unchanged and the balance (under $1.00) left unpaid; no write-off. Gated by `orders.closeSmallBalance`.
       */
      z.object({ mode: z.literal('small_balance') }),
      z.object({ mode: z.literal('write_off'), adjustmentId: AdjustmentId, reasonText: z.string().min(1).max(500) }),
    ]),
  }),
  ...orderOutput,
  permission: { session: 'staff' },
  risk: 'medium',
  emits: [OrderClosed, AdjustmentApplied, OrderPriced],
  errors: [...editErrors, BalanceDue, CapabilityUnavailable],
  dryRun: true,
  agent: { expose: false },
  legacy: ['POST /v1/orders/{id}/complete'],
});

export const ReopenOrder = defineCommand({
  ...device, ...quiet, ...onOrder,
  name: 'orders.reopen',
  summary: 'Reopen a closed order.',
  input: z.object({ orderId: OrderId, reason: z.string().min(1).max(500).optional() }),
  ...orderOutput,
  permission: { session: 'staff' },
  emits: [OrderReopened],
  errors: [OrderNotFound, OrderRuleViolation],
});

export const ApplyAdjustment = defineCommand({
  ...device, ...quiet, ...onOrder,
  name: 'orders.applyAdjustment',
  summary:
    'Put a discount, comp or service charge on a line or on the order: a preset, a custom amount or percent, or an automatic discount (Happy Hour) the register applied when the line was rung. One discount per line and one on the order.',
  input: z.object({
    orderId: OrderId,
    adjustmentId: AdjustmentId,
    kind: z.enum(['discount', 'comp', 'service_charge']),
    scope: z.enum(['order', 'line']),
    lineId: OrderLineId.optional(),
    basis: AdjustmentBasis,
    reason: z.string().max(500).optional(),
    /** Which discount it is (automatic, a preset, or custom): the Close Drawer report's Discount Summary splits on it. */
    source: DiscountSource.optional(),
  }),
  ...orderOutput,
  permission: { session: 'staff' },
  emits: [AdjustmentApplied, OrderPriced],
  errors: editErrors,
  legacy: ['POST /v2/orders (order_discounts, item discounts)', 'PUT /v3/orders/{id}'],
});

export const RemoveAdjustment = defineCommand({
  ...device, ...quiet, ...onOrder,
  name: 'orders.removeAdjustment',
  summary: 'Take a discount, comp or service charge off an order (a write-off is never removed).',
  input: z.object({ orderId: OrderId, adjustmentId: AdjustmentId }),
  ...orderOutput,
  permission: { session: 'staff' },
  emits: [AdjustmentRemoved, OrderPriced],
  errors: editErrors,
  legacy: ['PUT /v3/orders/{id}'],
});

/**
 * The business-day close (CD-B04): an order still open at its business day's end. Paid in full it closes; partly paid it
 * closes with the balance written off and flagged for review; unpaid it is abandoned (audited, never a silent void).
 * Run by the location's scheduled job at the business-day start, with a deterministic command id.
 */
export const AutoCloseOrder = defineCommand({
  ...onOrder,
  availability: 'location',
  origin: 'any',
  name: 'orders.autoClose',
  summary: "Close an order left open at its business day's end: paid closes, partly paid is written off for review, unpaid is abandoned.",
  input: z.object({ orderId: OrderId, adjustmentId: AdjustmentId }),
  ...orderOutput,
  permission: 'orders.forceComplete',
  risk: 'medium',
  dryRun: true,
  agent: { expose: false },
  emits: [OrderClosed, AdjustmentApplied, OrderPriced, OrderAbandoned],
  errors: editErrors,
  legacy: ['maintenance:auto-close-orders'],
});

/**
 * Legacy Force Complete Order (DLG-073; `GET /v1/orders/{id}/force-complete`, DrawerOrders.vue): Radish staff complete an
 * open order from its drawer. Shipped as legacy's result (core FORCE_COMPLETE 'legacy'): closed as it stands, forced, its
 * balance still owed. The correction ('reconciled', off) settles it as the business-day close does. Legacy's GET wrote
 * with no permission or location check (B571, L730); this is a write command under the permission and the location's scope.
 */
export const ForceCompleteOrder = defineCommand({
  ...onOrder,
  availability: 'location',
  origin: 'any',
  name: 'orders.forceComplete',
  summary: 'Complete an open order from its drawer as legacy does: closed as it stands, any balance still owed (the reconciling correction is off).',
  input: z.object({ orderId: OrderId, adjustmentId: AdjustmentId }),
  ...orderOutput,
  permission: 'orders.forceComplete',
  risk: 'high',
  dryRun: true,
  agent: { expose: false },
  emits: [OrderClosed, AdjustmentApplied, OrderPriced, OrderAbandoned],
  errors: editErrors,
  legacy: ['GET /v1/orders/{id}/force-complete'],
});

// ---- online orders (ops plan 2026-10-13-online-orders-in-reports) --------------------------------------------------------

/** An online order's line as the online math stored it (core `OnlineSaleLine`); its id is core's `onlineSaleLineId`. */
const OnlineSaleLine = z
  .object({
    lineId: OrderLineId,
    productId: ProductId,
    name: z.string().min(1).max(255),
    unitPrice: Money,
    quantity: z.int().min(1).max(999),
    modifiers: z.array(z.object({ name: z.string().min(1).max(120), unitPrice: Money, count: z.int().min(0).max(999) })).max(200),
    note: z.string().max(500).nullable(),
    subtotal: Money,
  })
  .meta({ id: 'OnlineSaleLine' });

/**
 * The register that moves an online order records it on the location's node as an ordinary order (target-model §1.6;
 * legacy wrote an `orders` row with `source = 'online'`), so every report counts it as legacy's did. Decided on the node,
 * which holds the stream lock: a retry or a second register at once records one order. The money is the online order's
 * stored money (never repriced), and the outcome follows its status: picked up completes it, a reject, cancel or void
 * voids it, anything else leaves it open.
 */
export const RecordOnlineSale = defineCommand({
  ...onOrder, ...quiet,
  availability: 'location',
  origin: 'any',
  name: 'orders.recordOnlineSale',
  summary: "Record an online order on the location as an order (its stored lines and totals) and complete or void it as its status says.",
  input: z.object({
    orderId: OrderId,
    locationId: LocationId,
    /** The accepting register's open till; on a reject before any accept, the location's earliest-opened open till. */
    tillSessionId: TillSessionId,
    /** The location's "Online Pickup" dining option, when it has one (legacy CreateParentOrder). */
    diningOptionId: z.string().min(1).max(64).optional(),
    /**
     * B1584: a QR Table Pay order's table, shown in Order Lookup and the order detail as legacy's tab shows it. It never
     * seats the order (OrderOpened v5 `online.tableId`, never `tableId`).
     */
    tableId: TableId.optional(),
    number: z.string().min(1).max(32),
    placedAt: EpochMs,
    lines: z.array(OnlineSaleLine).min(1).max(200),
    subtotal: Money,
    tax: Money,
    tip: Money,
    /** What the online card paid on the order, ex tip; zero when it was not paid online. */
    paid: Money,
    outcome: z.enum(['open', 'complete', 'void']),
    reasonText: z.string().max(500).optional(),
    /** The employee who moved it at the register (the Voids report names who voided it). */
    employeeId: EmployeeId.optional(),
  }),
  ...orderOutput,
  // The register calls it at its node with its device credential (as the online order calls in the cloud).
  permission: { session: 'device' },
  emits: [OrderOpened, LineAdded, OrderPriced, SentToKitchen, OrderClosed, OrderVoided],
  errors: [OrderTillNotOpen, OrderRuleViolation],
  legacy: ['POST /v2/orders (online: CreateParentOrder)', 'PUT /v1/online-orders/{id}/status', 'POST /v1/orders/{id}/void2 (online)'],
});

// ---- check commands (W-45, W-46) -------------------------------------------------------------------------------------

export const SplitChecksEvenly = defineCommand({
  ...device, ...quiet, ...onOrder,
  name: 'orders.splitEvenly',
  summary: 'Split Payment: split the order into equal checks (one per id). Only while no payment is applied and the order is not split.',
  input: z.object({ orderId: OrderId, checkIds: z.array(CheckId).min(2).max(99) }),
  ...orderOutput,
  permission: { session: 'staff' },
  emits: [ChecksSplit, OrderPriced],
  errors: editErrors,
  legacy: ['PUT /v3/orders/{id} (split {type: even, count})'],
});

export const AddCheck = defineCommand({
  ...device, ...quiet, ...onOrder,
  name: 'orders.addCheck',
  summary: 'Add Check: an unsplit order becomes check 1 (every line) and a new empty check; a split one gets one more check.',
  input: z.object({ orderId: OrderId, checkId: CheckId, firstCheckId: CheckId }),
  ...orderOutput,
  permission: { session: 'staff' },
  emits: [ChecksSplit, CheckAdded, OrderPriced],
  errors: editErrors,
  legacy: ['PUT /v3/orders/{id} (split {type: item, count}, items[].ticket)'],
});

export const RemoveCheck = defineCommand({
  ...device, ...quiet, ...onOrder,
  name: 'orders.removeCheck',
  summary: 'Remove Check: remove an empty check with no payment; the last check left makes the order one check again.',
  input: z.object({ orderId: OrderId, checkId: CheckId }),
  ...orderOutput,
  permission: { session: 'staff' },
  emits: [CheckRemoved, OrderPriced],
  errors: editErrors,
});

export const MoveLines = defineCommand({
  ...device, ...quiet, ...onOrder,
  name: 'orders.moveLines',
  summary:
    'Split Check Editor: move lines, or part of a multi-quantity line (as a new line), to another check. A check holding a payment is locked.',
  input: z.object({
    orderId: OrderId,
    toCheckId: CheckId,
    moves: z
      .array(z.object({ lineId: OrderLineId, quantity: Qty, newLineId: OrderLineId.optional(), newAdjustmentId: AdjustmentId.optional() }))
      .min(1),
  }),
  ...orderOutput,
  permission: { session: 'staff' },
  emits: [LinesMovedBetweenChecks, OrderPriced],
  errors: editErrors,
  legacy: ['PUT /v3/orders/{id} (items[].ticket)'],
});

export const CombineChecks = defineCommand({
  ...device, ...quiet, ...onOrder,
  name: 'orders.combineChecks',
  summary: 'Combine Checks (or Unsplit Payment): every line back on one check; payments on the checks then pay the whole order.',
  input: z.object({ orderId: OrderId }),
  ...orderOutput,
  permission: { session: 'staff' },
  emits: [ChecksMerged, OrderPriced],
  errors: editErrors,
  legacy: ['POST /v1/orders/{order_id}/reset-tickets'],
});

/**
 * WF-40 Combine Orders: the order (the destination) adopts the target orders by id, in one event on its own stream.
 * The targets' streams are not written (a command writes one stream, platform #713); readers treat each as combined.
 */
export const CombineOrders = defineCommand({
  ...device, ...quiet, ...onOrder,
  name: 'orders.combineOrders',
  summary:
    'Combine Orders: the order adopts the target orders\' lines and payments; it keeps its own number, name, dining option, tax profile, cart discount and gratuity, and is re-priced. Each target then reads as combined into it.',
  input: z.object({ orderId: OrderId, targetOrderIds: z.array(OrderId).min(1).max(50) }),
  ...orderOutput,
  permission: { session: 'staff' },
  emits: [OrdersAdopted, OrderPriced],
  // Round 3's rollout gate: refused while a register active at the location doesn't read OrdersAdopted.
  errors: [...editErrors, CapabilityUnavailable],
  legacy: ['POST /v1/orders/{order_id}/combine'],
});

/**
 * Cutover's carry-over step (lane X): the imported open order `orderId` becomes a live order, decided by core's
 * CarryOverTab from the stream's OrderImported snapshot, or refused (`carry_over_refused`) so the tab is closed in legacy.
 * The cloud alone runs it, as the import (deterministic command id per legacy order, so a resumed cutover replays).
 */
export const CarryOverTab = defineCommand({
  ...quiet, ...onOrder,
  availability: 'cloud',
  cloudReason: 'The cutover step writes it from the cloud’s imported history, before the location’s node takes authority.',
  origin: 'cloud',
  name: 'orders.carryOverTab',
  summary: 'Cutover: an imported open legacy tab becomes a live order, priced as legacy’s register prices a reopened cart and held to legacy’s stored total to the cent.',
  input: z.object({ orderId: OrderId }),
  ...orderOutput,
  permission: 'settings.platform.manage',
  emits: [OrderCarriedOver, AdjustmentApplied, OrderPriced],
  errors: editErrors,
  legacy: [],
});

/** The split-check commands, registered in the catalog as one group. */
export const checkCommands = [SplitChecksEvenly, AddCheck, RemoveCheck, MoveLines, CombineChecks, CombineOrders] as const;

// ---- queries ---------------------------------------------------------------------------------------------------------
/** One table with a live order seated at it (legacy floor-plan item `status = used` with its order summary). */
export const SeatedTable = z
  .object({
    tableId: TableId,
    orderId: OrderId,
    number: z.string(),
    tabName: z.string().nullable(),
    /** Null for an order no employee owns: an online order (B1611). */
    ownerEmployeeId: EmployeeId.nullable(),
    openedAt: IsoInstant,
    lineCount: z.int().nonnegative(),
    total: Money,
  })
  .meta({ id: 'SeatedTable' });

/** A drawer or server bank open at the location, with what one server has open on it (legacy order-transfer-drawer-list). */
export const TransferTill = z
  .object({
    tillSessionId: TillSessionId,
    tillLetter: z.string(),
    ownerEmployeeId: EmployeeId,
    openOrderCount: z.int().nonnegative(),
    openOrdersTotal: Money,
  })
  .meta({ id: 'TransferTill' });

export const ListTransferTills = defineQuery({
  name: 'orders.transferTills.list',
  summary: "Where an order can be transferred: the location's open drawers and server banks, with what the given server has open on each.",
  input: z.object({ locationId: LocationId, employeeId: EmployeeId }),
  output: z.object({ tills: z.array(TransferTill) }),
  permission: { session: 'staff' },
  availability: 'location',
  scope: 'location',
  errors: [],
  agent: { expose: false },
  legacy: ['GET /v1/drawers/order-transfer-drawer-list', 'GET /v1/drawers/{id}/transfer-drawers'],
});

export const ListSeatedTables = defineQuery({
  name: 'orders.seatedTables.list',
  summary: "The location's occupied tables: each table with the live order seated at it.",
  input: z.object({ locationId: LocationId }),
  output: z.object({ tables: z.array(SeatedTable) }),
  permission: { session: 'staff' },
  availability: 'location',
  scope: 'location',
  errors: [],
  agent: { expose: true, surfaces: ['backoffice.assistant', 'crew.manager'] },
  legacy: ['GET /v1/floor-plans', 'GET /v2/floor-plans'],
});

export const GetOrder = defineQuery({
  name: 'orders.get',
  summary: 'Read one order with its lines, total and what is paid.',
  input: z.object({ orderId: OrderId }),
  ...orderOutput,
  permission: { session: 'staff' },
  availability: 'location',
  scope: 'location',
  errors: [OrderNotFound],
  agent: { expose: true, surfaces: ['backoffice.assistant', 'crew.manager'] },
  legacy: ['GET /v1/orders/{id}'],
});

/**
 * A160 (ops plan 2026-10-20-order-numbering, Decided 1; bug B660): the location's next order number, from its one
 * counter (`orders.order_numbers`), taken under that counter's row lock, as legacy's API numbers every order
 * (OrderActions.php:42-58). The hub answers it when the store has one, the cloud when it has none. A register asks it
 * when it opens an order and fixes the answer on the order; one that gets no answer in about a second numbers the order
 * in its own prefixed series instead. Every call takes a number, as an order open does: a number taken and never used
 * is a gap, never a repeat. So a client must never retry it by itself (no automatic retry on a timeout or a network
 * error): a retry takes a second number. `businessDate` is the date the number belongs to (each business date starts at 1), so a
 * register on the other side of the day's turn can tell the answer is not for its order.
 */
export const NextOrderNumber = defineQuery({
  name: 'orders.nextNumber',
  summary: "Take the location's next order number for the business date now, from the location's one counter.",
  input: z.object({ locationId: LocationId }),
  output: z.object({ businessDate: LocalDate, number: OrderNumber }),
  permission: { session: 'device' },
  availability: 'location',
  scope: 'location',
  errors: [],
  agent: { expose: false },
  legacy: ['POST /v1/orders'],
});
